import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext, useParams, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Seo from "../../components/Seo";
import { Avatar, EmptyState, Modal, Spinner } from "../../components/lawyer/LawyerUi";
import StripeCardModal from "../../components/user/StripeCardModal";
import DirectCall from "../../components/call/DirectCall";
import { lawyerTitle } from "../../components/user/LawyerCard";
import dummyLawyer from "../../assets/images/dummyLawyer.jpeg";
import socketService from "../../services/SocketService";
import { apiErrorMessage, isBlankFile, userService } from "../../services/userApi";

const money = (v) => Number((Number(v) || 0).toFixed(2)).toLocaleString();

const names = (list) => (Array.isArray(list) ? list.map((i) => i?.name || i).filter(Boolean) : []);

const languageNames = (value) => {
  if (!value) return [];
  const list = Array.isArray(value) ? value : String(value).split(",");
  return list.map((l) => (typeof l === "string" ? l.trim() : l?.name)).filter(Boolean);
};

// Paragraph clamped to a few lines with a "See More" toggle, as on the app.
const Expandable = ({ text, lines = 2 }) => {
  const [open, setOpen] = useState(false);
  if (!text) return null;
  return (
    <div className="usp-expand">
      <p className={open ? "" : "clamped"} style={{ WebkitLineClamp: lines }}>
        {text}
      </p>
      {text.length > 90 && (
        <button type="button" className="usp-link-btn" onClick={() => setOpen((o) => !o)}>
          {open ? "See Less" : "See More"}
        </button>
      )}
    </div>
  );
};

const Stars = ({ value }) => (
  <span className="lwp-stars" aria-label={`${value} out of 5`}>
    {[1, 2, 3, 4, 5].map((n) => (
      <i key={n} className={`bi ${value >= n ? "bi-star-fill" : value >= n - 0.5 ? "bi-star-half" : "bi-star"}`} />
    ))}
  </span>
);

const PERIODS = [
  { key: "weekly", label: "One Time Service", price: (l) => l.weekly_price },
  { key: "monthly", label: "Monthly", price: (l) => l.monthly_price || (Number(l.weekly_price) || 0) * 4 },
];

const UserLawyerDetails = () => {
  const { lawyerId } = useParams();
  const navigate = useNavigate();
  const { profile } = useOutletContext();
  const [lawyer, setLawyer] = useState(null);
  const [myService, setMyService] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [slide, setSlide] = useState(0);
  const [online, setOnline] = useState(false);
  const [period, setPeriod] = useState("weekly");
  const [optionsOpen, setOptionsOpen] = useState(false);
  const [payOpen, setPayOpen] = useState(false);
  const [openingChat, setOpeningChat] = useState(false);
  const [params, setParams] = useSearchParams();
  // ?call=1 (e.g. the old /user/video-call?lawyer= link) opens the call sheet straight away.
  const [callOpen, setCallOpen] = useState(params.get("call") === "1");

  const closeCall = () => {
    setCallOpen(false);
    if (params.has("call")) {
      params.delete("call");
      setParams(params, { replace: true });
    }
  };

  useEffect(() => {
    let active = true;
    setLoading(true);
    userService
      .getLawyerDetails(lawyerId)
      .then((res) => {
        if (!active) return;
        if (!res?.data?.status) throw new Error(res?.data?.message);
        setLawyer(res.data.data);
        setMyService(res.data.my_service || null);
        setOnline(Number(res.data.data?.is_online) === 1);
      })
      .catch((err) => active && setError(apiErrorMessage(err, err?.message || "Unable to load this lawyer.")))
      .finally(() => active && setLoading(false));
    return () => {
      active = false;
    };
  }, [lawyerId]);

  // Live online/offline from the socket server while the profile is open.
  useEffect(() => {
    const presence = { type: "lawyer", id: Number(lawyerId) };
    const off = socketService.subscribe("presence-update", (p) => {
      if (p?.type === "lawyer" && String(p.id) === String(lawyerId)) setOnline(!!p.online);
    });
    socketService.emit("watch-presence", presence);
    return () => {
      off();
      socketService.emit("unwatch-presence", presence);
    };
  }, [lawyerId]);

  if (loading) {
    return (
      <div className="lwp-page">
        <Spinner />
      </div>
    );
  }
  if (error || !lawyer) {
    return (
      <div className="lwp-page">
        <EmptyState icon="bi-person-x" text={error || "Lawyer not found."} />
      </div>
    );
  }

  const images = [...(lawyer.images || [])].filter((u) => !isBlankFile(u));
  if (!images.length) images.push(isBlankFile(lawyer.picture) ? dummyLawyer : lawyer.picture);
  const categories = [...names(lawyer.categories), ...names(lawyer.sub_categories)];
  const jurisdictions = names(lawyer.jurisdictions);
  const languages = languageNames(lawyer.languages);
  const services = Array.isArray(lawyer.services) ? lawyer.services.filter(Boolean) : [];
  const reviews = lawyer.reviews || [];
  const rating = Number(lawyer.rating) || 0;
  const location = [lawyer.city, lawyer.country].filter(Boolean).join(", ");
  const current = PERIODS.find((p) => p.key === period);
  const price = current.price(lawyer);
  const activeService = myService && myService.status === "Active" ? myService : null;
  const teamLawyers = lawyer.lawyers || [];

  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: lawyerTitle(lawyer), url });
      else {
        await navigator.clipboard.writeText(url);
        toast.success("Link copied");
      }
    } catch {
      // Share sheet dismissed.
    }
  };

  const openChat = async (userServiceId) => {
    setOpeningChat(true);
    try {
      const res = await userService.openServiceChat(userServiceId);
      const chatId = res?.data?.data?.chat?.id;
      if (!chatId) throw new Error(res?.data?.message);
      navigate(`/user/chat/${chatId}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Unable to open the chat."));
    } finally {
      setOpeningChat(false);
    }
  };

  const onPaid = async (pi) => {
    const res = await userService.buyService({ lawyerId: lawyer.id, period, transactionId: pi.id });
    if (!res?.data?.status) throw new Error(res?.data?.message || "Payment received but the service couldn't be activated. Please contact support.");
    toast.success(res.data.message || "Service purchased successfully!");
    const chatId = res.data.chat?.id;
    setTimeout(() => navigate(chatId ? `/user/chat/${chatId}` : "/user/my-lawyers"), 0);
    return true;
  };

  return (
    <div className="lwp-page usp-lawyer-page">
      <Seo title={`${lawyerTitle(lawyer)} | Legal Platform`} noindex />

      <div className="usp-gallery">
        <div className="usp-gallery-track" style={{ transform: `translateX(-${slide * 100}%)` }}>
          {images.map((src, i) => (
            <img key={src + i} src={src} alt={`${lawyerTitle(lawyer)} ${i + 1}`} onError={(e) => (e.currentTarget.src = dummyLawyer)} />
          ))}
        </div>
        <button type="button" className="usp-gallery-btn left" onClick={() => navigate(-1)} aria-label="Back">
          <i className="bi bi-chevron-left" />
        </button>
        <button type="button" className="usp-gallery-btn right" onClick={share} aria-label="Share">
          <i className="bi bi-box-arrow-up" />
        </button>
        {images.length > 1 && (
          <>
            <button
              type="button"
              className="usp-gallery-nav prev"
              onClick={() => setSlide((s) => (s - 1 + images.length) % images.length)}
              aria-label="Previous photo"
            >
              <i className="bi bi-chevron-left" />
            </button>
            <button
              type="button"
              className="usp-gallery-nav next"
              onClick={() => setSlide((s) => (s + 1) % images.length)}
              aria-label="Next photo"
            >
              <i className="bi bi-chevron-right" />
            </button>
            <div className="usp-gallery-dots">
              {images.map((_, i) => (
                <button
                  key={i}
                  type="button"
                  className={i === slide ? "active" : ""}
                  onClick={() => setSlide(i)}
                  aria-label={`Photo ${i + 1}`}
                />
              ))}
            </div>
          </>
        )}
      </div>

      <div className="usp-lawyer-info">
        {location && <p className="usp-lawyer-location">{location}</p>}
        <h1>{lawyerTitle(lawyer)}</h1>
        <p className="usp-lawyer-sub">
          {categories.length > 0 && (
            <span>
              {categories[0]}
              {categories.length > 1 ? ` +${categories.length - 1}` : ""}
            </span>
          )}
          <span className="usp-lawyer-rating">
            {rating || "New"} <i className="bi bi-star-fill" />
          </span>
        </p>

        <button
          type="button"
          className={`usp-online${online ? " is-online" : ""}`}
          disabled={!online}
          onClick={() => setCallOpen(true)}
        >
          <span className="usp-online-icon">
            <i className="bi bi-telephone-inbound-fill" />
          </span>
          <span>
            <strong>{online ? "Lawyer is online" : "Lawyer is currently offline"}</strong>
            <small>Connect with a Lawyer Now</small>
          </span>
          {online && <i className="bi bi-chevron-right ms-auto" />}
        </button>

        <Expandable text={lawyer.about} lines={2} />

        {jurisdictions.length > 0 && (
          <section className="usp-lawyer-section">
            <h3>Jurisdictions</h3>
            <Expandable text={jurisdictions.join(", ")} lines={1} />
          </section>
        )}
        {categories.length > 0 && (
          <section className="usp-lawyer-section">
            <h3>Expertise</h3>
            <Expandable text={categories.join(", ")} lines={1} />
          </section>
        )}
        {services.length > 0 && (
          <section className="usp-lawyer-section">
            <h3>Services</h3>
            <ul className="usp-bullets">
              {services.map((s, i) => (
                <li key={i}>{s}</li>
              ))}
            </ul>
          </section>
        )}
        {languages.length > 0 && (
          <section className="usp-lawyer-section">
            <h3>Languages</h3>
            <p>{languages.join(", ")}</p>
          </section>
        )}
        {teamLawyers.length > 0 && (
          <section className="usp-lawyer-section">
            <h3>Our Lawyers</h3>
            <div className="usp-team">
              {teamLawyers.map((t) => (
                <Link key={t.id} to={`/user/lawyers/${t.id}`} className="usp-team-item">
                  <Avatar src={t.picture} alt={t.name} size={56} />
                  <span>{t.name}</span>
                </Link>
              ))}
            </div>
          </section>
        )}
        <section className="usp-lawyer-section">
          <h3>
            Reviews {reviews.length > 0 && <small>({reviews.length})</small>}
          </h3>
          {reviews.length === 0 ? (
            <p className="lwp-muted">No reviews yet.</p>
          ) : (
            <ul className="usp-reviews">
              {reviews.slice(0, 10).map((r) => (
                <li key={r.id}>
                  <Avatar src={r.user?.picture} alt={r.user?.name} size={40} />
                  <div>
                    <strong>{r.user?.name || "Client"}</strong>
                    <Stars value={Number(r.stars) || 0} />
                    {r.comment && <p>{r.comment}</p>}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </section>
      </div>

      <div className="usp-buy-bar">
        {activeService ? (
          <>
            <div className="usp-buy-row">
              <span>
                Active {activeService.period === "monthly" ? "monthly subscription" : "service"}
              </span>
            </div>
            <button type="button" className="usp-buy-btn" onClick={() => openChat(activeService.id)} disabled={openingChat}>
              {openingChat ? <span className="spinner-border spinner-border-sm" /> : (
                <>
                  <i className="bi bi-chat-dots-fill" /> Open Chat
                </>
              )}
            </button>
          </>
        ) : (
          <>
            <button type="button" className="usp-buy-row" onClick={() => setOptionsOpen(true)}>
              <span>
                $ {money(price)}/ {current.label}
              </span>
              <span className="usp-buy-options">
                2 options <i className="bi bi-chevron-down" />
              </span>
            </button>
            <button type="button" className="usp-buy-btn" onClick={() => setOptionsOpen(true)} disabled={!(Number(price) > 0)}>
              <i className="bi bi-credit-card-fill" /> Get Service
            </button>
          </>
        )}
      </div>

      <Modal
        open={optionsOpen}
        onClose={() => setOptionsOpen(false)}
        title="Select pricing option"
        className="usp-options-modal"
        footer={
          <button
            type="button"
            className="usp-buy-btn dark"
            onClick={() => {
              setOptionsOpen(false);
              setPayOpen(true);
            }}
          >
            <i className="bi bi-credit-card-fill" /> Get Service
          </button>
        }
      >
        <div className="usp-options">
          {PERIODS.map((p) => (
            <button
              key={p.key}
              type="button"
              className={`usp-option${period === p.key ? " active" : ""}`}
              onClick={() => setPeriod(p.key)}
              aria-pressed={period === p.key}
            >
              <span className="usp-option-check">
                <i className="bi bi-check-lg" />
              </span>
              <span>
                $ {money(p.price(lawyer))}/ {p.label}
              </span>
            </button>
          ))}
        </div>
      </Modal>

      <StripeCardModal
        open={payOpen}
        onClose={() => setPayOpen(false)}
        title="Get Service"
        amountLabel={`$ ${money(price)} · ${current.label}`}
        submitLabel={`Pay $ ${money(price)}`}
        billing={{ name: profile?.name, email: profile?.email }}
        prepare={() => userService.servicePayment(lawyer.id, price)}
        onPaid={onPaid}
      />

      <DirectCall lawyer={lawyer} profile={profile} open={callOpen} onClose={closeCall} />
    </div>
  );
};

export default UserLawyerDetails;
